import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Paperclip, Plus, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { addDays, today } from '@mkcore/shared';
import { EXPENSE_VAT_CODES, RECEIPT_TYPES, createExpense, deleteExpense, restoreExpense } from '@mkcore/billing/schema';
import { Badge, Button, Card, Checkbox, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr, toast } from '@mkcore/ui';
import { ApiError, newIdempotencyKey } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';
import { useActionMutation, useErrorMessage, UNDO_TOAST_MS } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { expenseCategoriesQuery, expensesQuery } from '@/features/billing/queries';
import { inputToCents } from '@/features/engagements/money';
import { ProjectSelect } from '@/features/time/pickers';

const search = z.object({ month: z.string().optional() });
export const Route = createFileRoute('/_app/billing/expenses')({ validateSearch: search, component: Expenses });

const monthOf = (d: string) => d.slice(0, 7);
const monthStart = (m: string) => `${m}-01`;
const shiftMonth = (m: string, by: number) => {
  const y = Number(m.slice(0, 4));
  const mo = Number(m.slice(5, 7)) - 1 + by;
  return `${y + Math.floor(mo / 12)}-${String((((mo % 12) + 12) % 12) + 1).padStart(2, '0')}`;
};
const monthEnd = (m: string) => addDays(monthStart(shiftMonth(m, 1)), -1);

/** Expenses per month (CO-0368): an entry form on top, the expenses with their receipt and totals. */
function Expenses() {
  const { t, i18n } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const me = useMe();
  const canCreate = useCan('billing:create');
  const canManage = useCan('billing:manage');
  const month = s.month ?? monthOf(today());
  const q = useInfiniteQuery(expensesQuery({ from: monthStart(month), to: monthEnd(month), limit: 100 }));
  const categories = useQuery(expenseCategoriesQuery);
  const labelOf = (v: string) => categories.data?.items.find((c) => c.value === v)?.label ?? v;
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const totals = q.data?.pages[0]?.totals;
  const invalidate = [['billing', 'expense']];
  const restore = useActionMutation(restoreExpense, { invalidate, success: t('expenses.restored') });
  const remove = useActionMutation(deleteExpense, {
    invalidate,
    silentErrors: true,
    onSuccess: (e) => toast(t('expenses.deleted'), { duration: UNDO_TOAST_MS, action: { label: t('common.restore'), onClick: () => restore.mutate({ input: { id: e.id } }) } }),
  });
  const monthLabel = new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${monthStart(month)}T12:00:00Z`));
  return (
    <>
      <PageHeader title={t('expenses.title')} description={t('expenses.description')} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" aria-label={t('mileage.prevMonth')} onClick={() => void navigate({ search: { month: shiftMonth(month, -1) }, replace: true })}>
          <ChevronLeft />
        </Button>
        <span className="min-w-40 text-sm font-bold capitalize">{monthLabel}</span>
        <Button variant="ghost" size="icon" aria-label={t('mileage.nextMonth')} onClick={() => void navigate({ search: { month: shiftMonth(month, 1) }, replace: true })}>
          <ChevronRight />
        </Button>
        <Button variant="secondary" size="sm" disabled={month === monthOf(today())} onClick={() => void navigate({ search: {}, replace: true })}>
          {t('mileage.thisMonth')}
        </Button>
        {totals && (
          <div className="ml-auto flex flex-wrap gap-x-5 text-sm text-fg-2" data-testid="expense-totals">
            <span>
              {t('expenses.total')}: <span className="font-bold tabular-nums text-fg-1">{formatMoney(totals.totalCents)}</span>
            </span>
            <span>
              {t('expenses.withoutVat')}: <span className="font-bold tabular-nums text-fg-1">{formatMoney(totals.amountCents)}</span>
            </span>
            <span>
              {t('expenses.billableAmount')}: <span className="font-bold tabular-nums text-fg-1">{formatMoney(totals.billableAmountCents)}</span>
            </span>
          </div>
        )}
      </div>
      {canCreate && <NewExpense month={month} />}
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-48" />
      ) : !rows.length ? (
        <EmptyState title={t('expenses.empty')} description={canCreate ? t('expenses.emptyHint') : undefined} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('time.fields.date')}</Th>
              <Th>{t('invoices.fields.description')}</Th>
              <Th className="hidden md:table-cell">{t('expenses.fields.category')}</Th>
              <Th className="hidden sm:table-cell">{t('time.fields.project')}</Th>
              <Th className="text-right">{t('expenses.total')}</Th>
              <Th className="hidden text-right md:table-cell">{t('expenses.fields.vat')}</Th>
              <Th>{t('expenses.receipt')}</Th>
              <Th className="sr-only">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((e) => (
              <Tr key={e.id}>
                <Td className="tabular-nums">{formatDate(e.date)}</Td>
                <Td>
                  {e.description}
                  {e.supplier && <span className="text-fg-3"> · {e.supplier}</span>}{' '}
                  {e.billable ? (
                    <Badge tone="success">{e.markupBp ? t('expenses.billableMarkup', { pct: e.markupBp / 100 }) : t('mileage.billable')}</Badge>
                  ) : null}
                  {e.locked ? (
                    <Badge tone="neutral" className="ml-1">
                      {t(`expenses.status.${e.status}`)}
                    </Badge>
                  ) : null}
                </Td>
                <Td className="hidden md:table-cell">{labelOf(e.category)}</Td>
                <Td className="hidden sm:table-cell">{e.projectName ?? e.engagementName ?? e.organisationName ?? <span className="text-fg-3">{t('mileage.noClient')}</span>}</Td>
                <Td className="text-right tabular-nums">{formatMoney(e.totalCents, e.currency)}</Td>
                <Td className="hidden text-right tabular-nums md:table-cell">{formatMoney(e.vatCents, e.currency)}</Td>
                <Td>
                  <Receipt expenseId={e.id} url={e.receiptUrl} canUpload={!e.deletedAt && (e.userId === me.id || canManage)} />
                </Td>
                <Td className="text-right">
                  {!e.locked && (e.userId === me.id || canManage) && (
                    <Button size="icon" variant="ghost" aria-label={t('common.delete')} onClick={() => remove.mutate({ input: { id: e.id } }, { onError: (err) => toast.error(message(err)) })}>
                      <Trash2 />
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </>
  );
}

/** The receipt of an expense: a link to it, or an upload (the raw file, PUT). */
function Receipt({ expenseId, url, canUpload }: { expenseId: string; url: string | null; canUpload: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file: File) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/v1/billing/expenses/${expenseId}/receipt`, { method: 'PUT', headers: { 'content-type': file.type }, body: file, credentials: 'same-origin' });
      if (!res.ok) throw await ApiError.from(res);
      toast.success(t('expenses.receiptAdded'));
      void qc.invalidateQueries({ queryKey: ['billing', 'expense'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t('expenses.receiptFailed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-2">
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className="text-sm underline">
          {t('expenses.view')}
        </a>
      )}
      {canUpload && (
        <>
          <input ref={input} type="file" accept={RECEIPT_TYPES.join(',')} className="hidden" aria-label={t('expenses.upload')} onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} />
          <Button size="icon" variant="ghost" loading={busy} aria-label={url ? t('expenses.replace') : t('expenses.upload')} onClick={() => input.current?.click()}>
            <Paperclip />
          </Button>
        </>
      )}
    </span>
  );
}

/** The entry form: date, description, supplier, category, amount with VAT, project, passed on with markup. */
function NewExpense({ month }: { month: string }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const categories = useQuery(expenseCategoriesQuery);
  const todayDate = today();
  const [date, setDate] = useState(month === monthOf(todayDate) ? todayDate : monthStart(month));
  const [description, setDescription] = useState('');
  const [supplier, setSupplier] = useState('');
  const [category, setCategory] = useState('');
  const [total, setTotal] = useState('');
  const [vatCode, setVatCode] = useState<(typeof EXPENSE_VAT_CODES)[number]>('standard');
  const [projectId, setProjectId] = useState('');
  const [billable, setBillable] = useState<boolean | null>(null);
  const [markup, setMarkup] = useState('');
  const [key, setKey] = useState(newIdempotencyKey);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => setDate(month === monthOf(todayDate) ? todayDate : monthStart(month)), [month, todayDate]);
  useEffect(() => {
    if (!category && categories.data?.items[0]) setCategory(categories.data.items[0].value);
  }, [categories.data, category]);
  const create = useActionMutation(createExpense, {
    invalidate: [['billing', 'expense']],
    silentErrors: true,
    success: t('expenses.recorded'),
    onSuccess: () => {
      setDescription('');
      setSupplier('');
      setTotal('');
      setMarkup('');
      setErrors({});
      setKey(newIdempotencyKey());
    },
  });
  const cents = inputToCents(total);
  const markupBp = markup ? Math.round(Number(markup.replace(',', '.')) * 100) : 0;
  const passedOn = billable ?? !!projectId;
  const submit = () => {
    if (cents === null || cents <= 0) {
      setErrors({ totalCents: t('expenses.amountInvalid') });
      return;
    }
    create.mutate(
      {
        input: { date, description, supplier, category, totalCents: cents, vatCode, ...(projectId && { projectId }), billable: passedOn, ...(passedOn && markupBp > 0 && { markupBp }) },
        key,
      },
      { onError: (e) => setErrors(e instanceof ApiError ? { ...e.fieldErrors(), _: message(e) } : { _: message(e) }) },
    );
  };
  return (
    <Card className="mb-4 p-3">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label={t('time.fields.date')} className="w-40" error={errors.date}>{(p) => <Input {...p} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
        <Field label={t('invoices.fields.description')} className="min-w-48 flex-1" error={errors.description}>{(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
        <Field label={t('expenses.fields.supplier')} className="w-40">{(p) => <Input {...p} value={supplier} onChange={(e) => setSupplier(e.target.value)} />}</Field>
        <Field label={t('expenses.fields.category')} className="w-40" error={errors.category}>
          {(p) => (
            <NativeSelect {...p} value={category} onChange={(e) => setCategory(e.target.value)}>
              {(categories.data?.items ?? []).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('expenses.fields.total')} className="w-32" error={errors.totalCents}>{(p) => <Input {...p} inputMode="decimal" placeholder="12,10" value={total} onChange={(e) => setTotal(e.target.value)} />}</Field>
        <Field label={t('expenses.fields.vat')} className="w-32">
          {(p) => (
            <NativeSelect {...p} value={vatCode} onChange={(e) => setVatCode(e.target.value as typeof vatCode)}>
              {EXPENSE_VAT_CODES.map((v) => (
                <option key={v} value={v}>
                  {t(`expenses.vat.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('time.fields.project')} className="w-56" error={errors.projectId}>{(p) => <ProjectSelect {...p} value={projectId} emptyLabel={t('mileage.noClient')} onChange={setProjectId} />}</Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={passedOn} onCheckedChange={(v) => setBillable(v === true)} /> {t('expenses.passOn')}
        </label>
        {passedOn && <Field label={t('expenses.fields.markup')} className="w-24">{(p) => <Input {...p} inputMode="decimal" placeholder="0" value={markup} onChange={(e) => setMarkup(e.target.value)} />}</Field>}
        <Button type="submit" loading={create.isPending} disabled={!total || !description.trim() || !category}>
          <Plus /> {t('expenses.record')}
        </Button>
        {errors._ && (
          <p role="alert" className="basis-full text-sm text-danger">
            {errors._}
          </p>
        )}
      </form>
    </Card>
  );
}
